'use client';

import { useRouter } from 'next/navigation';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { RouteObject } from '~/app/types';
import { useNav } from '~/contexts/NavContext';
import { Icon } from '~/shared/components/Icon';
import { cn } from '~/shared/utils/cn';

const iconWrapperSizeClasses = {
  default: 'h-4.5 w-4.5',
  compact: 'h-8 w-8',
} as const;

type IconWrapperSize = keyof typeof iconWrapperSizeClasses;

type MenuItemRowProps = {
  title?: string;
  leftIcon?: React.ReactNode;
  isActive?: boolean;
  hasSubmenu?: boolean;
  isSubMenuItem?: boolean;
  iconWrapperSize?: IconWrapperSize;
  onClick?: () => void;
  isSubMenuOpen?: boolean;
};

function MenuItemRow({
  title,
  leftIcon,
  isActive,
  hasSubmenu,
  isSubMenuItem,
  iconWrapperSize = 'default',
  onClick,
  isSubMenuOpen,
}: MenuItemRowProps) {
  return (
    <li className="list-none">
      <button
        type="button"
        onClick={onClick}
        data-app-menu-item
        aria-current={isActive ? 'page' : undefined}
        aria-haspopup={hasSubmenu ? 'menu' : undefined}
        aria-expanded={hasSubmenu ? !!isSubMenuOpen : undefined}
        className={cn(
          'flex h-12 w-full cursor-pointer items-center gap-4 rounded px-4 text-left text-sm font-normal leading-5 text-[#191F2E]',
          isActive ? 'bg-[#F2DC39]' : 'bg-transparent',
          hasSubmenu ? 'justify-between' : 'justify-start',
          isSubMenuItem ? 'hover:bg-[#ededef]' : 'hover:bg-[#F2DC39]',
        )}
      >
        <span className="flex items-center justify-center gap-4">
          {leftIcon ? (
            <span
              className={cn(
                'relative inline-flex shrink-0 items-center justify-center overflow-visible',
                iconWrapperSizeClasses[iconWrapperSize]
              )}
            >
              {leftIcon}
            </span>
          ) : null}
          {title ? <span>{title}</span> : null}
        </span>
        {hasSubmenu ? (
          <Icon icon="ChevronRight" size={14} color="#191F2E" />
        ) : null}
      </button>
    </li>
  );
}

export function MobileMenu() {
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuItems,
    footerItems,
    toggleAppMenu,
    checkIfIsActive,
    toggleSubMenu,
  } = useNav();
  const router = useRouter();
  const onMenuItemClick = (item: RouteObject) => {
    if (item.routeComponents?.length) {
      toggleSubMenu(item);
    } else {
      toggleSubMenu(null);
      toggleAppMenu();
    }
  };

  const closeMenu = () => {
    toggleAppMenu();
    toggleSubMenu(null);
  };

  return (
    <DialogPrimitive.Root open={isAppMenuOpen} modal={false}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          onClick={toggleAppMenu}
          aria-label="Menu"
          aria-expanded={isAppMenuOpen}
          className="rounded-full border-none bg-transparent p-1.5 outline-none hover:bg-[#ededef]"
        >
          <Icon icon="Menu" />
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <div
          onClick={closeMenu}
          className="fixed inset-0 z-1300 bg-[#00000040]"
          aria-hidden
        />
        <DialogPrimitive.Content
          aria-label="Main"
          className="fixed left-0 top-(--app-banner-height,0px) z-1400 flex h-[calc(100vh-var(--app-banner-height,0px))] w-55 flex-col justify-between border-0 border-r border-r-[#EEEFF2] bg-white p-0 shadow-[8px_0px_16px_0px_#191F2E3D] focus:outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            Menu
          </DialogPrimitive.Title>
          <div className="h-18 border-b border-[#EEEFF2] p-0">
            {subMenuOpen?.length ? (
              <div className="flex h-18 w-full items-center gap-2 p-2">
                <span className="font-semibold">
                  {subMenuOpen[0]?.title || ''}
                </span>
              </div>
            ) : (
              <div className="flex h-18 w-full items-center justify-between p-2">
                <span
                  className="bg-[linear-gradient(90deg,#D3A238_3.33%,#F5E003_40.91%)] bg-clip-text text-2xl font-bold text-transparent"
                  style={{ WebkitTextFillColor: 'transparent' }}
                >
                  POLICY APPROVALS
                </span>
                <button
                  type="button"
                  onClick={() => {}}
                  className="rounded-full border-none bg-transparent p-1.5 outline-none hover:bg-[#ededef]"
                >
                  <Icon icon="Grip" />
                </button>
              </div>
            )}
          </div>
          {subMenuOpen?.length ? (
            <ul
              aria-label={subMenuOpen[0]?.title || 'Menu'}
              className="flex h-full w-full flex-col items-stretch justify-start gap-2 p-2"
            >
              {subMenuOpen[0]?.routeComponents?.map(subitem => (
                <MenuItemRow
                  key={subitem.key}
                  title={subitem.title}
                  leftIcon={subitem.icon}
                  isSubMenuItem
                  onClick={() => subitem.path && router.push(subitem.path)}
                />
              ))}
            </ul>
          ) : (
            <ul
              aria-label="Main"
              className="flex h-full w-full flex-col items-stretch justify-start gap-2 p-2"
            >
              {menuItems.map(item => (
                <MenuItemRow
                  key={item.key}
                  title={item.title}
                  leftIcon={item.icon}
                  hasSubmenu={!!item.routeComponents?.length}
                  isSubMenuOpen={subMenuOpen?.some(m => m?.key === item.key)}
                  isActive={checkIfIsActive(
                    item.path ?? '',
                    item.routeComponents
                  )}
                  onClick={() => item.path && router.push(item.path)}
                />
              ))}
            </ul>
          )}
          <div className="border-t border-[#EEEFF2] p-2">
            {footerItems.map(item => (
              <MenuItemRow
                key={item.key}
                title={item.title}
                hasSubmenu={!!item?.routeComponents?.length}
                isSubMenuOpen={subMenuOpen?.some(m => m?.key === item.key)}
                leftIcon={item.icon}
                iconWrapperSize="compact"
                isActive={checkIfIsActive(item.path ?? '', item.routeComponents)}
                onClick={() => {
                  if (item.routeComponents?.length) {
                    onMenuItemClick(item);
                  } else if (item.path) {
                    router.push(item.path);
                  }
                }}
              />
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
